import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Check, Hourglass, Lock } from "lucide-react";
import { toast } from "sonner";
import { api, fmt } from "../lib/api";
import { FacebookGroupCTA } from "../components/FacebookGroupCTA";
import { useAuth } from "../lib/auth";
import Countdown from "../components/Countdown";

export default function Pricing() {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(null);
  const [waitEmail, setWaitEmail] = useState("");
  const [autoStarted, setAutoStarted] = useState(false);
  const { user } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const autoPlan = params.get("auto");

  // Capture affiliate referral code from URL
  useEffect(() => {
    const ref = params.get("ref");
    if (ref) {
      sessionStorage.setItem("affiliate_ref", ref.trim().toUpperCase());
    }
  }, [params]);

  useEffect(() => {
    api.get("/public/state").then((r) => setState(r.data));
  }, []);

  const startCheckout = useCallback(async (plan) => {
    if (!user) { nav(`/signup?plan=${plan}`); return; }
    if (!state?.doors_open) { toast.error("Doors are closed. Join the waitlist."); return; }
    if (!state?.stripe_real) { toast.error("Stripe is not configured correctly. Checkout is unavailable."); return; }
    setBusy(plan);
    try {
      const payload = { plan, origin_url: window.location.origin };
      const ref = sessionStorage.getItem("affiliate_ref");
      if (ref) payload.ref = ref;
      const { data } = await api.post("/checkout/subscription", payload);
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout");
      setBusy(null);
    }
  }, [nav, state, user, setBusy]);

  useEffect(() => {
    if (!autoPlan || autoStarted || !state) return;
    if (!user) {
      nav(`/signup?plan=${autoPlan}`);
      return;
    }
    if (!state.doors_open) {
      toast.error("Doors are closed. Join the waitlist.");
      return;
    }
    setAutoStarted(true);
    startCheckout(autoPlan);
  }, [autoPlan, autoStarted, user, state, nav, startCheckout]);

  const joinWaitlist = async (e) => {
    e.preventDefault();
    try {
      await api.post("/public/waitlist", { email: waitEmail });
      toast.success("You're on the waitlist. We'll reach out when a seat opens.");
      setWaitEmail("");
    } catch (error) {
      toast.error("Could not join waitlist");
    }
  };

  if (!state) return <div className="min-h-screen flex items-center justify-center"><Hourglass className="w-6 h-6 text-gold animate-glow" /></div>;

  const fullMonthly = state.current_full_monthly_cents;
  const promo = state.launched && state.promo_remaining_seconds > 0;
  const perDay = (fullMonthly / 100 / 30).toFixed(2);
  const annualSave = Math.max(0, fullMonthly * 12 - state.annual_cents);
  const annualPct = fullMonthly * 12 ? Math.round((annualSave / (fullMonthly * 12)) * 100) : 0;
  const monthsFree = fullMonthly ? Math.round(annualSave / fullMonthly) : 0;

  const plans = [
    {
      key: "full_monthly",
      name: "Sovereign Monthly",
      price: fmt.money(fullMonthly),
      cadence: "/mo",
      tag: promo ? "FOUNDER PRICE" : "STANDARD",
      desc: "Full access. Community. All drops. The Activation Codes PDF.",
      features: ["All Saturday drops", "Sovereign Community access", "Notes, bookmarks, quizzes", "Activation Codes PDF instant download", "Monthly quick-win asset", "Monthly executive summary"],
      highlight: true,
    },
    {
      key: "full_annual",
      name: "Sovereign Annual",
      price: fmt.money(state.annual_cents),
      cadence: "/yr",
      tag: "BEST VALUE",
      desc: "12 months. Locked in. Auto-renew.",
      features: ["Everything in Monthly", `${monthsFree} month${monthsFree === 1 ? "" : "s"} free vs monthly — save ${fmt.money(annualSave)} (${annualPct}% off)`, "Decide once, live from it all year", "Priority on a-la-carte drops"],
    },
  ];
  // Foundational ($11) is intentionally NOT shown on the public sales page.
  // It only appears inside the cancel flow as a softer landing.

  return (
    <div className="min-h-screen py-24 px-6 lg:px-10">
      <div className="max-w-7xl mx-auto">
        <div className="overline mb-4">// THE SOVEREIGN'S TABLE &middot; {state.active_members} of {state.cap} seats filled</div>
        <h1 className="font-display text-5xl md:text-7xl text-cream leading-none">Stop waiting for permission.</h1>
        <p className="mt-6 text-textMuted text-lg max-w-2xl">
          Mammon says <em className="text-cream/80">"not enough."</em> Chronos says <em className="text-cream/80">"too late."</em> Both are lying. For about <span className="text-cream">${perDay} a day</span> &mdash; less than the coffee you buy without a second thought &mdash; you take your seat at a table where money becomes a courier and time bends to obedience. This isn't a cost. It's the end of a tax you've been paying for years.
        </p>

        {/* Honest scarcity — a real table with real capacity, not a fake meter */}
        <div className="mt-8 max-w-xl">
          <div className="flex items-center justify-between text-xs font-mono text-textMuted mb-2">
            <span>{state.active_members} stewards seated</span>
            <span>{Math.max(0, state.cap - state.active_members)} of {state.cap} remain</span>
          </div>
          <div className="h-2 bg-borderGold relative overflow-hidden">
            <div className="absolute inset-y-0 left-0 bg-gold transition-all" style={{ width: `${Math.min(100, Math.round((state.active_members / Math.max(1, state.cap)) * 100))}%` }} />
          </div>
          <p className="text-textDim text-xs mt-2">A real table has a real number of seats. We cap at {state.cap} so the room stays a brotherhood, not a stadium.</p>
        </div>

        {promo && (
          <div className="mt-10 inline-flex flex-col gap-3 p-5 border border-borderGold bg-surface">
            <div className="overline">// THIS IS YOUR KAIROS WINDOW</div>
            <Countdown seconds={state.promo_remaining_seconds} />
            <div className="text-xs font-mono text-textMuted max-w-xs">Founder pricing rewards those who refuse to delay. It rises to {fmt.money(state.after_promo_monthly_cents)}/mo after — not a trick, simply honor for moving in the appointed moment.</div>
          </div>
        )}

        {!state.doors_open && (
          <div className="mt-10 p-6 border border-ruby/60 bg-ruby/10">
            <div className="overline text-cream mb-2">// DOORS ARE CLOSED</div>
            <p className="text-cream/80 mb-4">All 300 seats are taken. Join the waitlist and you'll be first when a seat opens.</p>
            <form onSubmit={joinWaitlist} className="flex flex-col md:flex-row gap-3 max-w-md">
              <input data-testid="waitlist-email" type="email" required placeholder="you@dominion.com" value={waitEmail} onChange={(e) => setWaitEmail(e.target.value)} />
              <button data-testid="waitlist-submit" type="submit" className="btn-gold">Join the waitlist</button>
            </form>
          </div>
        )}

        {autoPlan && (
          <div className="mt-10 rounded border border-gold/20 bg-gold/5 p-5 text-sm text-cream">
            {user
              ? "You're already signed in. Completing Stripe checkout now is the last step before access is granted."
              : "Sign up first and you'll be sent directly into Stripe checkout to complete the purchase before access is activated."}
          </div>
        )}

        {!state.stripe_real && (
          <div className="mt-10 p-6 border border-ruby/60 bg-ruby/10">
            <div className="overline text-cream mb-2">// STRIPE NOT CONFIGURED</div>
            <p className="text-cream/80">Checkout is disabled until a real Stripe secret key is installed in the backend. This prevents unpaid users from bypassing the flow.</p>
          </div>
        )}
        <div className="grid md:grid-cols-2 gap-px bg-borderGold mt-16">
          {plans.map((p) => (
            <div key={p.key} className={`bg-void p-8 md:p-10 ${p.highlight ? "ring-1 ring-gold" : ""}`}>
              <div className="flex items-center justify-between">
                <div className="overline">// {p.tag}</div>
                {p.highlight && <Hourglass className="w-4 h-4 text-gold animate-glow" />}
              </div>
              <h3 className="font-display text-3xl text-cream mt-4">{p.name}</h3>
              <div className="mt-4 flex items-baseline gap-2">
                <span className="font-display text-6xl bg-gold-grad">{p.price}</span>
                <span className="text-textMuted text-sm">{p.cadence}</span>
              </div>
              <p className="text-textMuted text-sm mt-3 leading-relaxed">{p.desc}</p>
              <ul className="mt-6 space-y-3">
                {p.features.map((f) => (
                  <li key={f} className="flex gap-3 text-sm text-cream/90">
                    <Check className="w-4 h-4 text-gold mt-0.5 shrink-0" /> <span>{f}</span>
                  </li>
                ))}
              </ul>
              <button
                data-testid={`buy-${p.key}`}
                disabled={busy === p.key || !state.doors_open || !state.stripe_real}
                onClick={() => startCheckout(p.key)}
                className={`mt-8 w-full ${p.highlight ? "btn-gold" : "btn-ghost"} justify-center disabled:opacity-50`}
              >
                {busy === p.key ? "Opening Stripe..." : !state.doors_open ? <><Lock className="w-4 h-4" /> Doors closed</> : "Claim this seat"}
              </button>
            </div>
          ))}
        </div>

        <div className="mt-10 flex flex-col sm:flex-row sm:items-center gap-4 p-5 border border-gold/20 bg-gold/5">
          <Check className="w-6 h-6 text-gold shrink-0" />
          <p className="text-cream/90 text-sm leading-relaxed">
            <span className="text-gold font-display text-lg">You stay sovereign.</span> Auto-renews so you never have to think about it &mdash; and you can pause, downgrade, or cancel in two clicks from your billing portal, anytime. No chains, no calls, no guilt. A steward is never trapped.
          </p>
        </div>

        {/* Objection handling — answered through the ethos, near the decision */}
        <div className="mt-12 grid md:grid-cols-2 gap-px bg-borderGold">
          <div className="bg-void p-6">
            <div className="overline mb-2">// "IS NOW THE RIGHT TIME?"</div>
            <p className="text-cream/85 text-sm leading-relaxed">The question itself is Chronos talking. There is no "later" seat at a better price or a readier you. Kairos is the moment obedience meets opportunity &mdash; and you're reading this. That's the sign you were waiting for.</p>
          </div>
          <div className="bg-void p-6">
            <div className="overline mb-2">// "CAN I AFFORD IT?"</div>
            <p className="text-cream/85 text-sm leading-relaxed">You're already paying far more in the tax of stagnation. At about ${perDay} a day, this costs less than the coffee you finish before noon &mdash; redirected from comfort into command. The real question Mammon fears: what is staying the same costing you?</p>
          </div>
        </div>

        <div className="mt-12 text-xs font-mono uppercase tracking-[0.25em] text-textDim">
          Auto-renew. Cancel, pause, or downgrade anytime from your billing portal. {state.stripe_real ? "Secured by Stripe." : "Stripe in dev mode — use Robin's real key in .env to go live."}
        </div>

        {state.fb_group?.show_on_pricing && (
          <div className="mt-20">
            <div className="overline mb-4">// NOT READY FOR A SEAT YET?</div>
            <FacebookGroupCTA fb={state.fb_group} />
          </div>
        )}
      </div>
    </div>
  );
}
